import React, { useState } from "react";
import axios from "axios";
import { Button, OutlineButton } from "components/button";
import { useModalContext } from "components/modal";
import { useSnackbarContext } from "components/snackbar";
import { useMutation, useQueryClient } from "react-query";
import Field from "components/field";
import DollarInputV2 from "components/input/dollarInputV2";
import { useTranslation } from "react-i18next";
import { dollarFormatter } from "utils";

type Props = {
  invoiceId: string;
  amountDue: number;
  customerName: string;
  isQboSynced?: boolean;
};

const WriteOffModal = ({ invoiceId, amountDue, customerName, isQboSynced = true }: Props) => {
  const { t } = useTranslation();
  const tBase = "containers.WriteOffModal";
  const tr = (key: string) => t(`${tBase}.${key}`);

  const modal = useModalContext();
  const snackbar = useSnackbarContext();
  const queryClient = useQueryClient();

  const [reason, setReason] = useState("Bad Debt Write-Off");
  const [writeOffDate, setWriteOffDate] = useState(new Date().toISOString().split("T")[0]);
  const [writeOffAmount, setWriteOffAmount] = useState(amountDue / 100); // Convert cents to dollars for display
  const [isLoading, setIsLoading] = useState(false);

  const writeOffMutation = useMutation(
    async () => {
      setIsLoading(true);
      return await axios.post(
        `${process.env.REACT_APP_SERVER_URL}/api/v1/company/quickbooks-online/write-off-bad-debt`,
        {
          invoiceId,
          reason,
          writeOffDate,
          writeOffAmount: Math.round(writeOffAmount * 100), // Convert dollars to cents for API
          invoiceSynced: isQboSynced ? true : false,
        }
      );
    },
    {
      onSuccess: (response) => {
        snackbar.showSnackbar(tr("Invoice successfully written off"));
        queryClient.invalidateQueries(["invoice", invoiceId]);
        modal.setModal();
      },
      onError: (error: any) => {
        console.error("Write-off error:", error);
        snackbar.showSnackbar(
          error.response?.data?.message || tr("Error writing off invoice"),
          "error"
        );
      },
      onSettled: () => {
        setIsLoading(false);
      },
    }
  );

  const handleWriteOff = () => {
    writeOffMutation.mutate();
  };

  return (
    <div className="p-6 max-w-md mx-auto">
      <div className="mb-6 -mt-4">
        {!isQboSynced ? (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">
                  {tr("Invoice Not Synced")}
                </h3>
                <div className="mt-2 text-sm text-red-700">
                  <p>
                    This invoice has not been synced to QuickBooks Online. Please sync the invoice first. Or continue to write off the invoice without syncing to QuickBooks Online.
                  </p>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <p className="text-gray-600 text-sm">
            {tr("This will create a credit memo in QuickBooks Online to write off the unpaid balance.")}
          </p>
        )}
      </div>

      <div className="space-y-4">
        <div className="bg-gray-50 p-4 rounded-lg">
          <div className="text-sm text-gray-600 mb-1">{tr("Customer")}</div>
          <div className="font-medium">{customerName}</div>
        </div>

        <div className="bg-gray-50 p-4 rounded-lg">
          <div className="text-sm text-gray-600 mb-1">{tr("Amount Due")}</div>
          <div className="font-medium text-red-600">
            {dollarFormatter(amountDue)}
          </div>
        </div>

        <div className="flex flex-col">
          <label className="text-sm font-medium text-gray-700 mb-1">
            {tr("Write-Off Amount")}
          </label>
          <DollarInputV2
            value={writeOffAmount}
            onValueChange={(values) => {
              setWriteOffAmount(values.floatValue || 0);
            }}
            placeholder={tr("Enter amount to write off")}
            min={0}
            max={amountDue / 100} // Convert cents to dollars for max validation
            step={0.01}
          />
        </div>

        <Field
          label={tr("Reason for Write-Off")}
          value={reason}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setReason(e.target.value)}
          placeholder={tr("Enter reason for write-off")}
        />

        <Field
          label={tr("Write-Off Date")}
          type="date"
          value={writeOffDate}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setWriteOffDate(e.target.value)}
        />

      </div>

      <div className="flex justify-end space-x-3 mt-6">
        <OutlineButton
          onClick={() => modal.setModal()}
          disabled={isLoading}
        >
          {tr("Cancel")}
        </OutlineButton>
        <Button
          primary
          onClick={handleWriteOff}
          isLoading={isLoading}
          disabled={!reason.trim() || writeOffAmount <= 0 || writeOffAmount > (amountDue / 100)}
        >
          {tr("Write Off Invoice")}
        </Button>
      </div>
    </div>
  );
};

export default WriteOffModal;